import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Card, Form, Button, ButtonGroup, Spinner, Alert, Badge, ListGroup, Row, Col, Accordion } from 'react-bootstrap';
import { API_URL } from '../api';
import { useGeolocation, getSavedRadius, getSavedProductsPerConcept } from '../hooks/useGeolocation';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer,
} from 'recharts';

// Leaflet icon fix
const defaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = defaultIcon;

// --- Types ---
interface ProductPrice {
  store_id: number | null;
  store_name: string;
  chain_name: string;
  price: number;
  unit_price: number | null;
  reference_unit: string | null;
  has_promotion: boolean;
  distance_m: number | null;
  location: { type: string; coordinates: [number, number] } | null;
  price_source: string;
  note: string | null;
  origin: string | null;
  source_url: string | null;
  last_updated: string | null;
}

interface ProductResult {
  product_id: number;
  name: string;
  brand: string | null;
  category: string | null;
  quantity: number | null;
  unit: string | null;
  prices: ProductPrice[];
}

interface ConceptGroup {
  concept_id: number;
  concept_name: string;
  icon: string | null;
  products: ProductResult[];
}

interface SearchResponse {
  concepts: ConceptGroup[];
  ungrouped: ProductResult[];
  total: number;
}

// --- Component ---
const ProductSearch: React.FC = () => {
  const [query, setQuery] = useState('');
  const [data, setData] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<ProductResult | null>(null);

  const { location, source: locationSource, error: locationError, loading: geoLoading } = useGeolocation();
  const radiusKm = Math.round(getSavedRadius() / 1000);
  const perConcept = getSavedProductsPerConcept();

  const search = useCallback(async () => {
    if (!query.trim() || !location) return;
    setLoading(true);
    setError(null);
    setData(null);
    setSelectedProduct(null);

    try {
      const params = new URLSearchParams({
        q: query.trim(),
        lat: location.lat.toString(),
        lng: location.lng.toString(),
        radius_km: radiusKm.toString(),
        limit: '10',
        per_concept: perConcept.toString(),
      });
      const res = await fetch(`${API_URL}/products/search?${params}`);
      if (!res.ok) throw new Error('Suche fehlgeschlagen');
      setData(await res.json());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [query, location, radiusKm]);

  const handleSubmit = (e: React.FormEvent) => { e.preventDefault(); search(); };

  const physicalPrices = selectedProduct?.prices.filter(p => p.price_source === 'physical') || [];
  const hasResults = data && (data.concepts.length > 0 || data.ungrouped.length > 0);

  return (
    <>
      <h4 className="mb-3">Produktmonitor</h4>

      {locationError && <Alert variant="warning" className="py-2">{locationError}</Alert>}

      <Card className="mb-3">
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                placeholder="z.B. Milch, Butter, Kaffee..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                disabled={loading || geoLoading || !location}
                autoFocus
              />
              <Button type="submit" variant="primary" disabled={loading || geoLoading || !location || !query.trim()}>
                {loading ? <Spinner size="sm" animation="border" /> : 'Suchen'}
              </Button>
            </div>
          </Form>
          {location && !geoLoading && (
            <div className="text-muted small mt-2">
              Standort: {location.lat.toFixed(4)}, {location.lng.toFixed(4)}
              {locationSource === 'home' && ' (Home)'}
              {locationSource === 'gps' && ' (GPS)'}
              {locationSource === 'fallback' && ' (Wien)'}
              {' '}&middot; Radius: {radiusKm} km
            </div>
          )}
        </Card.Body>
      </Card>

      {error && <Alert variant="danger">{error}</Alert>}
      {data && !hasResults && <Alert variant="info">Keine Produkte gefunden.</Alert>}

      {hasResults && (
        <Row>
          {/* Left: results */}
          <Col md={selectedProduct ? 6 : 12}>
            {/* Concept groups */}
            {data!.concepts.length > 0 && (
              <Accordion defaultActiveKey="0" className="mb-3">
                {data!.concepts.map((cg, idx) => {
                  const cheapest = cg.products[0]?.prices[0];
                  return (
                    <Accordion.Item key={cg.concept_id} eventKey={String(idx)}>
                      <Accordion.Header>
                        <div className="d-flex justify-content-between w-100 me-3 align-items-center">
                          <span>
                            {cg.icon && <span className="me-2">{cg.icon}</span>}
                            <strong>{cg.concept_name}</strong>
                            <small className="text-muted ms-2">({cg.products.length} Produkte)</small>
                          </span>
                          {cheapest && (
                            <span className="text-success fw-bold">
                              ab {cheapest.price.toFixed(2)} &euro;
                            </span>
                          )}
                        </div>
                      </Accordion.Header>
                      <Accordion.Body className="p-0">
                        <ListGroup variant="flush">
                          {cg.products.map(p => (
                            <ProductRow
                              key={p.product_id}
                              product={p}
                              isSelected={selectedProduct?.product_id === p.product_id}
                              onSelect={() => setSelectedProduct(
                                selectedProduct?.product_id === p.product_id ? null : p
                              )}
                            />
                          ))}
                        </ListGroup>
                      </Accordion.Body>
                    </Accordion.Item>
                  );
                })}
              </Accordion>
            )}

            {/* Ungrouped (trigram only) */}
            {data!.ungrouped.length > 0 && (
              <>
                {data!.concepts.length > 0 && (
                  <h6 className="text-muted mb-2">Weitere Treffer</h6>
                )}
                <ListGroup className="mb-3">
                  {data!.ungrouped.map(p => (
                    <ProductRow
                      key={p.product_id}
                      product={p}
                      isSelected={selectedProduct?.product_id === p.product_id}
                      onSelect={() => setSelectedProduct(
                        selectedProduct?.product_id === p.product_id ? null : p
                      )}
                    />
                  ))}
                </ListGroup>
              </>
            )}
          </Col>

          {/* Right: detail panel */}
          {selectedProduct && (
            <Col md={6}>
              <Card className="mb-3">
                <Card.Body>
                  <Card.Title>
                    {selectedProduct.name}
                    {selectedProduct.brand && <Badge bg="secondary" className="ms-2">{selectedProduct.brand}</Badge>}
                  </Card.Title>
                  {selectedProduct.category && (
                    <div className="text-muted small mb-3">{selectedProduct.category}</div>
                  )}
                  <ListGroup variant="flush">
                    {selectedProduct.prices.map((p, i) => (
                      <ListGroup.Item key={i} className="d-flex justify-content-between align-items-center px-0">
                        <div>
                          <strong>{p.store_name}</strong>
                          <span className="text-muted ms-1">({p.chain_name})</span>
                          <div className="text-muted small">
                            {p.distance_m != null
                              ? formatDist(p.distance_m)
                              : p.price_source === 'online_fallback' ? 'Online-Preis' : ''}
                          </div>
                          <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                            {priceProvenance(p)}
                          </div>
                        </div>
                        <div className="text-end">
                          <span className={`fw-bold ${i === 0 ? 'text-success fs-5' : ''}`}>
                            {p.price.toFixed(2)} &euro;
                          </span>
                          {p.unit_price != null && p.reference_unit && (
                            <div className="text-muted small">{p.unit_price.toFixed(2)} {p.reference_unit}</div>
                          )}
                          {p.has_promotion && <Badge bg="danger" className="ms-1">Aktion</Badge>}
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Card.Body>
              </Card>

              <PriceHistoryCard productId={selectedProduct.product_id} />

              {physicalPrices.length > 0 && location && (
                <Card className="mt-3">
                  <Card.Body className="p-0">
                    <div style={{ height: '280px' }}>
                      <MapContainer
                        center={[location.lat, location.lng]} zoom={13}
                        style={{ height: '100%', width: '100%' }}
                      >
                        <TileLayer
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        />
                        <Marker position={[location.lat, location.lng]}>
                          <Popup>Dein Standort</Popup>
                        </Marker>
                        {physicalPrices.map((p, i) => (
                          p.location && (
                            <Marker key={i} position={[p.location.coordinates[1], p.location.coordinates[0]]}>
                              <Popup>
                                <strong>{p.store_name}</strong><br />
                                {p.price.toFixed(2)} &euro;
                                {p.distance_m != null && <><br />{formatDist(p.distance_m)}</>}
                              </Popup>
                            </Marker>
                          )
                        ))}
                      </MapContainer>
                    </div>
                  </Card.Body>
                </Card>
              )}
            </Col>
          )}
        </Row>
      )}
    </>
  );
};

// --- Product row ---
const ProductRow: React.FC<{
  product: ProductResult;
  isSelected: boolean;
  onSelect: () => void;
}> = ({ product, isSelected, onSelect }) => {
  const best = product.prices[0];
  return (
    <ListGroup.Item action active={isSelected} onClick={onSelect}
      className="d-flex justify-content-between align-items-center">
      <div>
        <strong>{product.name}</strong>
        {product.brand && <small className="text-muted ms-2">{product.brand}</small>}
        {product.quantity && product.unit && (
          <small className="text-muted ms-1">
            {product.quantity}{product.unit === 'g' || product.unit === 'ml' ? product.unit : ` ${product.unit}`}
          </small>
        )}
      </div>
      <div className="text-end">
        {best && (
          <>
            <span className="fw-bold">{best.price.toFixed(2)} &euro;</span>
            {best.unit_price != null && best.reference_unit && (
              <small className={isSelected ? '' : 'text-muted'} style={{ marginLeft: 4 }}>
                ({best.unit_price.toFixed(2)} {best.reference_unit})
              </small>
            )}
            <small className={isSelected ? '' : 'text-muted'} style={{ marginLeft: 4 }}>
              @ {best.chain_name}
            </small>
            {best.has_promotion && <Badge bg="danger" className="ms-1" style={{ fontSize: '0.6em' }}>Aktion</Badge>}
          </>
        )}
      </div>
    </ListGroup.Item>
  );
};

function formatDist(m: number): string {
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km entfernt` : `${Math.round(m)} m entfernt`;
}

const ORIGIN_LABELS: Record<string, string> = {
  scrape_web: 'Online-Shop',
  scrape_flyer: 'Prospekt',
  ocr_receipt: 'Kassenbon',
  manual: 'manuell erfasst',
  api: 'API',
};

// Provenance line: "Billa Online-Shop · shop.billa.at · Stand 05.07.2026"
function priceProvenance(p: ProductPrice): string {
  const parts: string[] = [];
  const method = p.origin ? ORIGIN_LABELS[p.origin] ?? p.origin : null;
  parts.push([p.chain_name, method].filter(Boolean).join(' '));
  if (p.source_url) parts.push(p.source_url.replace(/^https?:\/\//, '').replace(/\/$/, ''));
  if (p.last_updated) parts.push('Stand ' + new Date(p.last_updated).toLocaleDateString('de-AT'));
  return parts.filter(Boolean).join(' · ');
}

// --- Price history chart ---

interface HistoryPoint {
  observed_at: string;
  price: number;
  is_promotion: boolean;
}

interface HistorySeries {
  store_id: number;
  store_name: string;
  chain_name: string;
  points: HistoryPoint[];
}

interface HistoryResponse {
  product_id: number;
  product_name: string;
  days: number;
  series: HistorySeries[];
}

const RANGE_OPTIONS: { label: string; days: number }[] = [
  { label: '30 T', days: 30 },
  { label: '90 T', days: 90 },
  { label: '1 J', days: 365 },
  { label: 'Alles', days: 3650 },
];

// Tableau 10 — perceptually distinct categorical palette. Assigned by
// series index (not store_id modulo) so the first few stores get max
// contrast from each other instead of collisions on adjacent IDs.
const COLORS = [
  '#4e79a7', // blue
  '#f28e2b', // orange
  '#e15759', // red
  '#59a14f', // green
  '#b07aa1', // purple
  '#76b7b2', // teal
  '#edc948', // yellow
  '#ff9da7', // pink
  '#9c755f', // brown
  '#bab0ac', // grey
];

const PriceHistoryCard: React.FC<{ productId: number }> = ({ productId }) => {
  const [days, setDays] = useState(90);
  const [data, setData] = useState<HistoryResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let aborted = false;
    setLoading(true);
    fetch(`${API_URL}/products/${productId}/price_history?days=${days}`)
      .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
      .then((d: HistoryResponse) => { if (!aborted) setData(d); })
      .catch(() => { if (!aborted) setData(null); })
      .finally(() => { if (!aborted) setLoading(false); });
    return () => { aborted = true; };
  }, [productId, days]);

  // Pivot per-series into a single { date, [series_key]: price } array sorted by date.
  const chartData = useMemo(() => {
    if (!data) return [];
    const buckets = new Map<string, Record<string, number | string>>();
    data.series.forEach(s => {
      const key = `${s.chain_name} — ${s.store_name}`;
      s.points.forEach(p => {
        const day = p.observed_at.slice(0, 10);
        let row = buckets.get(day);
        if (!row) { row = { date: day }; buckets.set(day, row); }
        row[key] = p.price;
      });
    });
    return Array.from(buckets.values()).sort(
      (a, b) => String(a.date).localeCompare(String(b.date))
    );
  }, [data]);

  const seriesKeys = useMemo(
    () => (data?.series ?? []).map((s, i) => ({
      key: `${s.chain_name} — ${s.store_name}`,
      color: COLORS[i % COLORS.length],
    })),
    [data]
  );

  const totalPoints = data?.series.reduce((n, s) => n + s.points.length, 0) ?? 0;

  return (
    <Card className="mt-3">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
          <Card.Title className="mb-0">Preisentwicklung</Card.Title>
          <ButtonGroup size="sm">
            {RANGE_OPTIONS.map(o => (
              <Button
                key={o.days}
                variant={days === o.days ? 'primary' : 'outline-secondary'}
                onClick={() => setDays(o.days)}
              >
                {o.label}
              </Button>
            ))}
          </ButtonGroup>
        </div>

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />
          </div>
        ) : !data || totalPoints === 0 ? (
          <Alert variant="info" className="mb-0 py-2">
            Keine Preisdaten im gewählten Zeitraum.
          </Alert>
        ) : (
          <>
            <div style={{ width: '100%', height: 280 }}>
              <ResponsiveContainer>
                <LineChart data={chartData} margin={{ top: 5, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => `${Number(v).toFixed(2)} €`}
                  />
                  <Tooltip
                    formatter={(value: any) => [`${Number(value).toFixed(2)} €`, '']}
                    labelFormatter={(l) => l}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {seriesKeys.map(s => (
                    <Line
                      key={s.key}
                      type="monotone"
                      dataKey={s.key}
                      stroke={s.color}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      connectNulls
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="text-muted small mt-1">
              {totalPoints} Beobachtungen über {data.series.length} {data.series.length === 1 ? 'Markt' : 'Märkte'}
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default ProductSearch;
